// Globals
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch } from '@/store';
import { createPhotoShootingPrice, deletePhotoShootingPrice, updatePhotoShootingPrice } from '../../feature';

// Components
import { ConfirmationModal } from '@/components';
import { Button, EmptyDataThumbnail, Spinner } from '@/ui';
import { PhotoShootingCard, PhotoShootingForm, StyledTitle } from '../../components';
import { StyledContent, StyledDrawer, StyledList, StyledWrapper } from './PhotoShootingPricesContainerStyles';

// Models
import { IPhotoShootingPrice } from '@/models/prices.model';

// Helpers
import { useModal } from '@/hooks';
import { getEntityId } from '@/utils';
import { TPhotoShootingFormValues } from '../../helpers';
import { useGetPhotoShootingPrice } from '../../hooks';

const PhotoShootingPricesContainer = () => {
  const { t } = useTranslation('prices');
  const { isLoading, prices } = useGetPhotoShootingPrice();
  const dispatch = useAppDispatch();
  const { isOpened, closeModal, openModal } = useModal();

  const [deletedId, setDeletedId] = useState<string | null>(null);
  const [editablePrice, setEditablePrice] = useState<IPhotoShootingPrice | null>(null);

  const handleClose = () => {
    setEditablePrice(null);
    closeModal();
    setDeletedId(null);
  };

  const handleUpdate = async (data: TPhotoShootingFormValues) => {
    try {
      if (editablePrice) {
        await dispatch(
          updatePhotoShootingPrice({
            ...data,
            id: getEntityId(editablePrice.PK),
          }),
        ).unwrap();
      } else {
        await dispatch(createPhotoShootingPrice(data)).unwrap();
      }
      handleClose();
    } catch (error) {}
  };

  const handleConfirmDelete = async () => {
    if (!deletedId) return;
    try {
      await dispatch(deletePhotoShootingPrice(getEntityId(deletedId))).unwrap();
      handleClose();
    } catch (error) {}
  };

  return (
    <StyledContent>
      <StyledWrapper>
        <StyledTitle>{t('title_photoshooting')}</StyledTitle>
        <Button type="primary" shape="round" onClick={openModal}>
          {t('button_create')}
        </Button>
      </StyledWrapper>
      <Spinner size="large" spinning={isLoading}>
        <StyledList>
          {!prices?.length && <EmptyDataThumbnail />}
          {prices?.map((price) => (
            <PhotoShootingCard key={price.PK} onDelete={setDeletedId} onEdit={setEditablePrice} item={price} />
          ))}
        </StyledList>
      </Spinner>
      <StyledDrawer
        footer={
          <Button shape="round" block type="primary" htmlType="submit" form="create-form" loading={isLoading}>
            {editablePrice ? t('button_update') : t('button_create')}
          </Button>
        }
        width="35rem"
        onClose={handleClose}
        open={isOpened || !!editablePrice}
        title={editablePrice ? t('button_update') : t('button_create')}
        destroyOnClose
      >
        <PhotoShootingForm
          formName="create-form"
          onSubmit={handleUpdate}
          initialValues={{
            description_en: editablePrice?.description_en,
            description_no: editablePrice?.description_no,
            from_area: editablePrice?.from_area,
            name_en: editablePrice?.name_en,
            name_no: editablePrice?.name_no,
            price: editablePrice?.price,
            property_type: editablePrice?.property_type,
            to_area: editablePrice?.to_area ?? undefined,
          }}
        />
      </StyledDrawer>
      <ConfirmationModal
        isOpened={!!deletedId}
        onCancel={handleClose}
        onConfirm={handleConfirmDelete}
        title={t('title_delete_price')}
      />
    </StyledContent>
  );
};

export default PhotoShootingPricesContainer;
